import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, CurrencyDollar, PaperPlaneTilt, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";

/**
 * OwnerApprovalCard — per-WO controls for the Owner Approval Threshold feature.
 * Reads tenant config: if disabled, the card hides entirely.
 * Reads job.owner_approval: shows current status (pending / approved / rejected).
 * Otherwise: shows a "Request Owner Approval" button that opens the request modal.
 */
export default function OwnerApprovalCard({ job, onReload }) {
  const [cfg, setCfg] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [dollars, setDollars] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/owner-approval").then(r => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  // Listen for auto-trigger from LandlordQuoteModal — when a quote crosses
  // the threshold, LandlordQuoteModal fires this event and we auto-open the modal.
  useEffect(() => {
    const handler = (e) => {
      if (!e.detail || e.detail.jobId !== job?.id) return;
      const cents = Number(e.detail.estimateCents || 0);
      if (cents <= 0) return;
      setDollars(String(Math.round(cents / 100)));
      setNote(cfg?.message_note || "");
      setModalOpen(true);
    };
    window.addEventListener("field-radar:auto-owner-approval", handler);
    return () => window.removeEventListener("field-radar:auto-owner-approval", handler);
  }, [job?.id, cfg?.message_note]);

  if (!cfg?.enabled) return null;  // feature toggled off tenant-wide

  const oa = job.owner_approval;
  const status = oa?.status;

  const openModal = () => {
    setDollars(String(Math.round((cfg.threshold_cents || 0) / 100)));
    setNote(cfg.message_note || "");
    setModalOpen(true);
  };

  const submit = async () => {
    const cents = Math.round(parseFloat(dollars || "0") * 100);
    if (cents <= 0) { toast.error("Enter an estimate amount"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${job.id}/request-owner-approval`, {
        estimate_cents: cents,
        note: note.trim(),
      });
      if (r.data.sent) {
        toast.success(`Approval request sent to ${r.data.to}`);
      } else {
        toast.warning(`Request logged but email delivery uncertain (${r.data.to})`);
      }
      setModalOpen(false);
      onReload?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Send failed"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="mt-6 rugged-card p-4 border-l-4 border-emerald-500 bg-emerald-50/30"
        data-testid="owner-approval-card">
        <div className="flex items-center gap-2 flex-wrap">
          <ShieldCheck weight="fill" size={16} className="text-emerald-600"/>
          <div className="font-display font-black text-sm uppercase tracking-wider text-emerald-900">
            Owner Approval
          </div>
          {status === "approved" && (
            <span className="ml-auto px-2 py-0.5 bg-emerald-600 text-white font-black uppercase text-[10px] tracking-widest rounded-sm inline-flex items-center gap-1"
              data-testid="owner-approval-status-approved">
              <CheckCircle weight="fill" size={11}/> Approved
            </span>
          )}
          {status === "rejected" && (
            <span className="ml-auto px-2 py-0.5 bg-red-600 text-white font-black uppercase text-[10px] tracking-widest rounded-sm inline-flex items-center gap-1"
              data-testid="owner-approval-status-rejected">
              <XCircle weight="fill" size={11}/> Rejected
            </span>
          )}
          {status === "pending" && (
            <span className="ml-auto px-2 py-0.5 bg-amber-500 text-white font-black uppercase text-[10px] tracking-widest rounded-sm inline-flex items-center gap-1"
              data-testid="owner-approval-status-pending">
              <Clock weight="fill" size={11}/> Awaiting Owner
            </span>
          )}
        </div>

        {status ? (
          <div className="mt-2 text-[12px] text-slate-700 space-y-0.5">
            <div>
              <b>${((oa.estimate_cents || 0) / 100).toLocaleString()}</b> requested from <b>{oa.landlord_name || oa.sent_to}</b>
              <span className="text-slate-500"> · {(oa.requested_at || "").slice(0, 10)}</span>
            </div>
            {oa.note && <div className="text-slate-600 italic">"{oa.note}"</div>}
            {oa.decided_at && (
              <div className="pt-1 border-t border-slate-200 mt-1">
                Decided <b>{oa.decision}</b> on {(oa.decided_at || "").slice(0, 10)}
                {oa.decision_note && <div className="mt-0.5 text-slate-600">"{oa.decision_note}"</div>}
              </div>
            )}
            <div className="pt-2 flex flex-wrap gap-2">
              <button onClick={openModal}
                data-testid="owner-approval-resend-btn"
                className="h-8 px-3 border-2 border-emerald-600 bg-white text-emerald-700 hover:bg-emerald-50 font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center gap-1">
                <PaperPlaneTilt weight="bold" size={11}/> Resend / Update
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-2 text-[12px] text-slate-700">
            <p>
              Fire off a one-click Approve/Reject email to the landlord before dispatching. Their decision gets stamped on this WO for legal cover.
            </p>
            <button onClick={openModal}
              data-testid="owner-approval-request-btn"
              className="mt-2 h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center gap-1.5 shadow-[3px_3px_0_#0f172a]">
              <PaperPlaneTilt weight="fill" size={12}/> Request Owner Approval
            </button>
            <span className="ml-2 text-[10px] text-slate-500">
              Threshold: <b>${Math.round((cfg.threshold_cents || 0) / 100).toLocaleString()}</b>
            </span>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
          onClick={() => setModalOpen(false)}
          data-testid="owner-approval-modal">
          <div className="w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-slate-200 bg-emerald-600 text-white">
              <div className="font-display font-black text-lg uppercase tracking-tight leading-none">Request Owner Approval</div>
              <div className="text-[10px] font-bold tracking-widest uppercase opacity-80 mt-1">
                One-click accept/reject email · legal record kept
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Estimate</label>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-slate-500 font-black">$</span>
                  <input type="number" min="1" step="10" value={dollars}
                    onChange={(e) => setDollars(e.target.value)}
                    data-testid="owner-approval-estimate-input"
                    className="flex-1 h-11 px-3 border-2 border-slate-300 rounded font-mono font-black text-lg focus:border-emerald-600 focus:outline-none"/>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Note to Owner (optional)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Add scope details, urgency, or timing..."
                  data-testid="owner-approval-note-input"
                  className="mt-1 w-full px-3 py-2 border-2 border-slate-300 rounded text-sm font-medium focus:border-emerald-600 focus:outline-none"/>
              </div>
              <div className="pt-2 flex gap-2 justify-end">
                <button onClick={() => setModalOpen(false)}
                  className="h-10 px-4 border-2 border-slate-300 bg-white text-slate-700 font-black uppercase text-xs tracking-widest rounded">
                  Cancel
                </button>
                <button onClick={submit} disabled={busy || !dollars}
                  data-testid="owner-approval-submit-btn"
                  className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center gap-1.5 shadow-[3px_3px_0_#0f172a]">
                  <PaperPlaneTilt weight="fill" size={12}/>
                  {busy ? "Sending…" : "Send Approval Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
